import { __exportAll } from "../_virtual/_rolldown/runtime.js";
import { getI9nItemWithId, getItemWithId, hasNotHiddenOrArchivedItemRefs } from "./item_array_utils.js";
//#region src/utils/mod_group_utils.ts
var mod_group_utils_exports = /* @__PURE__ */ __exportAll({
	getAddSubModsForModId: () => getAddSubModsForModId,
	getAutoSelectedSubModsForModId: () => getAutoSelectedSubModsForModId,
	getChoiceQuantityIncrement: () => getChoiceQuantityIncrement,
	getDeselectionSubModsForModId: () => getDeselectionSubModsForModId,
	getEffectiveModSelectedByDefaultQuantity: () => getEffectiveModSelectedByDefaultQuantity,
	getExtraSubModsForModId: () => getExtraSubModsForModId,
	getItemRefProperty: () => getItemRefProperty,
	getMaxAggregateQuantity: () => getMaxAggregateQuantity,
	getMaxSelectable: () => getMaxSelectable,
	getMinAggregateQuantity: () => getMinAggregateQuantity,
	getMinChoiceQuantity: () => getMinChoiceQuantity,
	getMinSelectable: () => getMinSelectable,
	getModMaxChoiceQuantity: () => getModMaxChoiceQuantity,
	getModSelectedByDefaultQuantity: () => getModSelectedByDefaultQuantity,
	getSubstitutionSubModsForModId: () => getSubstitutionSubModsForModId,
	getVisibleModIds: () => getVisibleModIds,
	isRequiredAndSingular: () => isRequiredAndSingular,
	modIsSelectedByDefault: () => modIsSelectedByDefault,
	modIsVisibleInModGroup: () => modIsVisibleInModGroup,
	shouldBeVisibleOnTheMenu: () => shouldBeVisibleOnTheMenu
});
function getAutoSelectedSubModsForModId(data, modId) {
	const modRef = getItemWithId(data, modId);
	if (modRef?.autoSelectedSubMods) return { collection: modRef.autoSelectedSubMods };
	return { collection: [] };
}
function getDeselectionSubModsForModId(data, modId) {
	const i9nModRef = getI9nItemWithId(data, modId);
	if (i9nModRef) return i9nModRef.deselectionSubMods || [];
	return [];
}
function getSubstitutionSubModsForModId(data, modId) {
	const i9nModRef = getI9nItemWithId(data, modId);
	if (i9nModRef) return i9nModRef.substitutionSubMods || [];
	return [];
}
function getAddSubModsForModId(data, modId) {
	const i9nModRef = getI9nItemWithId(data, modId);
	if (i9nModRef) return i9nModRef.addSubMods || [];
	return [];
}
function getExtraSubModsForModId(data, modId) {
	const i9nModRef = getI9nItemWithId(data, modId);
	if (i9nModRef) return i9nModRef.extraSubMods || [];
	return [];
}
function getItemRefProperty(data, modId, propertyName, defaultValue, overrideItems) {
	const itemRefs = overrideItems || data.items || [];
	const i9nItemRefs = data.i9nItems || [];
	for (const [i, itemRef] of itemRefs.entries()) if (itemRef._id.toString() === modId.toString()) {
		if (propertyName in itemRef) return itemRef[propertyName];
		if (i9nItemRefs.length > i) {
			const i9nItemRef = i9nItemRefs[i];
			if (i9nItemRef === void 0) throw new TypeError(`Missing i9n item reference at index ${i}`);
			if (propertyName in i9nItemRef) return i9nItemRef[propertyName];
		}
	}
	return defaultValue;
}
function modIsSelectedByDefault(data, modId, overrideItems) {
	return getItemRefProperty(data, modId, "selectedByDefaultQuantity", 0, overrideItems) > 0;
}
function getModSelectedByDefaultQuantity(data, modId, overrideItems) {
	return getItemRefProperty(data, modId, "selectedByDefaultQuantity", 0, overrideItems);
}
function getEffectiveModSelectedByDefaultQuantity(data, modId, overrideItems) {
	const docValue = getModSelectedByDefaultQuantity(data, modId, overrideItems);
	if (data.allowMultipleOfSameAddon && !data.disableAllowMultipleOfSameAddon) return docValue;
	return Math.min(docValue, 1);
}
function getVisibleModIds(data) {
	return data.items.filter((mod) => modIsVisibleInModGroup(data, mod._id)).map((mod) => mod._id);
}
function modIsVisibleInModGroup(data, itemId) {
	const itemRef = getItemWithId(data, itemId);
	if (!itemRef) return true;
	const i9nItemRef = getI9nItemWithId(data, itemId);
	return !itemRef.hideFromMenu && (!i9nItemRef || !i9nItemRef.hideFromMenu);
}
function isRequiredAndSingular(data) {
	if (data.items.filter((item) => modIsVisibleInModGroup(data, item._id)).length !== 1) return false;
	const minSelectable = Math.max(data.minSelectable ?? 0, data.posMinSelectableOverride ?? 0);
	const maxSelectable = Math.min(data.maxSelectable ?? 1066, data.posMaxSelectableOverride ?? 1066);
	if (minSelectable === 1 && maxSelectable === 1) return true;
	const minAggregateQuantity = Math.max(data.minAggregateQuantity ?? 0, data.posMinAggregateQuantityOverride ?? 0);
	const maxAggregateQuantity = Math.min(data.maxAggregateQuantity ?? 1066, data.posMaxAggregateQuantityOverride ?? 1066);
	if (minAggregateQuantity === 1 && maxAggregateQuantity === 1) return true;
	return false;
}
function shouldBeVisibleOnTheMenu(data, visibilityByItemId) {
	if (data.hideFromMenu) return false;
	return hasNotHiddenOrArchivedItemRefs(data, visibilityByItemId);
}
function getMinSelectable(modGroup) {
	return Math.max(modGroup.minSelectable || 0, modGroup.posMinSelectableOverride || 0);
}
function getMaxSelectable(modGroup) {
	return Math.min(modGroup.maxSelectable || 1066, modGroup.posMaxSelectableOverride || 1066);
}
function getMinAggregateQuantity(modGroup) {
	return Math.max(modGroup.minAggregateQuantity || 0, modGroup.posMinAggregateQuantityOverride || 0);
}
function getMaxAggregateQuantity(modGroup) {
	return Math.min(modGroup.maxAggregateQuantity || 1066, modGroup.posMaxAggregateQuantityOverride || 1066);
}
function getMinChoiceQuantity(modGroup) {
	if (modGroup.allowMultipleOfSameAddon && !modGroup.disableAllowMultipleOfSameAddon) return modGroup.minChoiceQuantity || 1;
	return 1;
}
function getChoiceQuantityIncrement(modGroup) {
	if (!modGroup.allowMultipleOfSameAddon || modGroup.disableAllowMultipleOfSameAddon) return 1;
	return modGroup.choiceQuantityIncrement || 1;
}
function getModMaxChoiceQuantity(modGroup, modId) {
	if (modGroup.allowMultipleOfSameAddon && !modGroup.disableAllowMultipleOfSameAddon) {
		const modRefMaxChoiceQuantity = getItemRefProperty(modGroup, modId, "maxChoiceQuantity", 0);
		if (modRefMaxChoiceQuantity > 0) return modRefMaxChoiceQuantity;
		return modGroup.maxChoiceQuantity || 1066;
	}
	return 1;
}
//#endregion
export { getAddSubModsForModId, getAutoSelectedSubModsForModId, getChoiceQuantityIncrement, getDeselectionSubModsForModId, getEffectiveModSelectedByDefaultQuantity, getExtraSubModsForModId, getItemRefProperty, getMaxAggregateQuantity, getMaxSelectable, getMinAggregateQuantity, getMinChoiceQuantity, getMinSelectable, getModMaxChoiceQuantity, getModSelectedByDefaultQuantity, getSubstitutionSubModsForModId, getVisibleModIds, isRequiredAndSingular, modIsSelectedByDefault, modIsVisibleInModGroup, mod_group_utils_exports, shouldBeVisibleOnTheMenu };

//# sourceMappingURL=mod_group_utils.js.map